import React from 'react';

import {ConnectorSizing, LinkerDomainVisualization, SharedDomainParams} from 'antibodies/AntibodyTypedefs';

import AntibodyComponentPopover from './AntibodyComponentPopover';

export type AntibodyLinkerProps = SharedDomainParams & {
  /* The color of the linker */
  color: string;
  domainVisualization: LinkerDomainVisualization;
  connectorSizing: ConnectorSizing;
};

/**
 * A long center line with a clockwise arc on each end, intended to link two `AntibodyDomain`.
 * This is an SVG component, so all units are 'abstract user units', relative to the SVG viewbox.
 * @param domainVisualization The visualization for the linker domain
 * @param connectorSizing The sizing params for this format's connectors
 * @param color The color of the line
 * @param proteinId The API ID of the protein entity associated with this domain. If passed, the linker will
 *                  be given a SmartChip-style popover with metadata about the protein.
 * @param tipLength The length of the tip after the arc
 */
export default function AntibodyLinker({
  domainVisualization,
  color,
  proteinId,
  inaccessible,
  connectorSizing,
}: AntibodyLinkerProps): JSX.Element {
  const {centerLength, endRadius, tipLength} = domainVisualization;
  const {strokeThickness} = connectorSizing;
  // Start from the lower end of the link.
  const start = `M 0 ${centerLength + endRadius - tipLength}`;
  // Move to the start of the arc.
  const toBottomArc = `L 0 ${centerLength + endRadius}`;
  // Move along an arc to the bottom of the center.
  const bottom = `A ${endRadius} ${endRadius} 0 0 0 ${2 * endRadius} ${centerLength + endRadius}`;
  // Move up along the center.
  const center = `L ${2 * endRadius} ${endRadius}`;
  // Move along an arc to the top of the upper end.
  const top = `A ${endRadius} ${endRadius} 0 0 1 ${4 * endRadius} ${endRadius}`;
  // Move to the upper end of the link.
  const end = `L ${4 * endRadius} ${endRadius + tipLength}`;
  return (
    <AntibodyComponentPopover proteinId={proteinId} inaccessible={inaccessible}>
      <path
        d={`${start} ${toBottomArc} ${bottom} ${center} ${top} ${end}`}
        stroke={color}
        strokeWidth={strokeThickness}
        fill="none"
      />
    </AntibodyComponentPopover>
  );
}
